import authStorage from "src/utils/authStorage";
import axios from "axios";

export const baseURL = () => {
  // Use a single environment variable for all environments
  if (process.env.REACT_APP_PRODUCTION_URL) {
    return process.env.REACT_APP_PRODUCTION_URL;
  }

  // In development, use port 3001 for the backend (Rails default)
  // This is a fallback if REACT_APP_PRODUCTION_URL is not set
  if (window.location.hostname === "localhost") {
    return "http://localhost:3001";
  }

  return window.location.origin;
};

// Derive WebSocket URL from base URL
export const wsURL = () => {
  const base = baseURL();
  // Ensure we don't have double slashes in the URL
  // Use wss:// for HTTPS URLs and ws:// for HTTP URLs
  let wsUrl =
    base
      .replace(/^https/, "wss")
      .replace(/^http/, "ws")
      .replace(/\/$/, "") + "/cable";

  // CRITICAL: Ensure wss:// for HTTPS pages to avoid Mixed Content errors
  if (
    typeof window !== "undefined" &&
    window.location.protocol === "https:" &&
    wsUrl.startsWith("ws://")
  ) {
    wsUrl = wsUrl.replace(/^ws:/, "wss:");
  }

  return wsUrl;
};

// Derive static assets URL from base URL
export const staticURL = () => {
  return baseURL();
};

const api = axios.create({
  // Ensure we don't have double slashes in the URL
  baseURL: `${baseURL().replace(/\/$/, "")}/api/v1`,
  // Rede de segurança: nenhum request pode pendurar pra sempre. Se o backend
  // travar (ex: chamada OpenAI síncrona), o axios aborta → a promise rejeita →
  // o componente trata o erro em vez de ficar no spinner eterno. 90s cobre o
  // pior caso do criar-assistente (chamadas OpenAI em série, 30s cada, já com
  // timeout no backend) sem abortar antes do backend resolver via fallback.
  timeout: 90000,
  // Ensure we have proper CORS headers for Heroku
  headers: {
    "Content-Type": "application/json",
    "Cache-Control": "no-cache",
  },
});

// Token do painel em toda chamada ao backend — inclusive no callback do Google (`googleAuth`), que desde
// 29/10/2026 só conecta a conta de quem está logado (o partnerId do corpo não escolhe mais em quem os tokens entram).
const withAuthorization = (config) => {
  const pathname = window.location.pathname;
  const tokenSales = localStorage.getItem("@sacgpt:sales-token");
  const user = JSON.parse(authStorage.getItem("@sacgpt:user") || "{}");
  const token = authStorage.getItem("@sacgpt:token");

  if (pathname.startsWith("/vendas") && tokenSales) {
    config.headers.Authorization = `Bearer ${tokenSales}`;
  } else if (user?.authToken) {
    config.headers.Authorization = `Bearer ${user.authToken}`;
  } else if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
};

api.interceptors.request.use(withAuthorization);

// Response interceptor to handle PHONE_REQUIRED error
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403 && error.response?.data?.code === "PHONE_REQUIRED") {
      // Store the flag - PhoneNumberModal in PrivateRoute handles the UI
      sessionStorage.setItem("@sacgpt:phoneRequired", "true");
    }
    if (error.response?.status === 403 && error.response?.data?.code === "PLAN_REQUIRED") {
      // Store the flag - PrivateRoute redirects to /escolher-plano
      sessionStorage.setItem("@sacgpt:planRequired", "true");
    }
    return Promise.reject(error);
  },
);

export const apiNoHead = axios.create({
  // Ensure we don't have double slashes in the URL
  baseURL: `${baseURL().replace(/\/$/, "")}/api/v1`,
});

export const googleAuth = axios.create({
  // Ensure we don't have double slashes in the URL
  baseURL: `${baseURL().replace(/\/$/, "")}/auth/google_oauth2`,
});
googleAuth.interceptors.request.use(withAuthorization);

export const devEnv = false;

export default api;
